import { ReactElement, useState, useContext, useMemo } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Pagination } from 'swiper/core';
import { AppContext } from '@context/AppContext';
import { ModalsContext } from '@root/context/ModalsContext';
import Availability from './Availability';
import OpeningHours from './OpeningHours';
import Amenities from './Amenities';
import Map from './Map';
import HostDetails from './HostDetails';
import ParagraphsExpand from '@root/components/Global/ParagraphsExpand';
import Stl from '@root/lib/Stl';

SwiperCore.use([ Pagination ]);

import style from './Detail.module.scss';
import Button from '../../Button/Button';
const st = (new Stl()).bind(style);
interface Props {
  listing: Dict;
  selectedDays: Date[];
}

const typeLabels = {
  'coworking-space': 'Coworking space',
  'office': 'Office',
  'hotel': 'Hotel',
  'restaurant': 'Restaurant',
};

export default function ListingDetail(props: Props): ReactElement {
  const router = useRouter();
  const appContext = useContext(AppContext);
  const modalContext = useContext(ModalsContext);
  const [ imageOverlayVisible, setImageOverlayVisible ] = useState(false);
  const [ forceRender, setForceRender ] = useState(0);
  const [ activeSlide, setActiveSlide ] = useState(0);
  const [ activeSlideLightBox, setActiveSlideLightBox ] = useState(0);

  const listing = props.listing;
  const listingLocation = listing.listings_locations[0];
  const totalWorkSpaces = listing.listings_workspaces.reduce((accu, currentValue) => accu + currentValue.amount, 0);

  const renderImages = useMemo(() => {
    return listing.listings_media.map((media, mediaIndex) => (
      <SwiperSlide
        key={ mediaIndex }
        className={ st`sliderSlide` }
        onClick={ () => { setActiveSlide(mediaIndex), setImageOverlayVisible(!imageOverlayVisible); } }>
        <img src={ media.url } />
      </SwiperSlide>
    ));
  }, [ listing.listings_media ] );

  const renderImagesLightbox = useMemo(() => {
    return listing.listings_media.map((media, mediaIndex) => {
      // const stylingClass = media.caption.length > 0 ? 'lightbox--with-caption' : '';

      return (
        <SwiperSlide key={ mediaIndex } className={ st`lightbox-slide` }>
          <div className={ st`lightbox-slide__img-container` } style={ { backgroundImage: `url("${ media.url }")` } }></div>
          {/* <img className={ st`lightbox-slide__img` } src={ media.url } /> */}
          <p className={ st`lightbox-slide__caption` }>{ media.caption }</p>
          {/* { media.caption.length > 0  && <p className={ st`lightbox__caption` }>{ media.caption }</p> } */}
        </SwiperSlide>
      );
    });
  }, [ listing.listings_media ]);

  return (
    <main
      className={ st`content ${ imageOverlayVisible && 'contentHidden' }` }>
      <div className={ st`slider` }>
        <Swiper
          key={ activeSlideLightBox + forceRender }
          loop={ true }
          loopedSlides= { listing.listings_media.length }
          slidesPerView={ 'auto' }
          centeredSlides={ true }
          spaceBetween={ 4 }
          updateOnImagesReady={ true }
          initialSlide= { activeSlideLightBox }
          onImagesReady= { () => {
            setForceRender(1); // Hacky fix: Because the Swiper (parent) isn't aware of the (SwiperSlide) child items we need to force a re-render.
          } }
        >
          { renderImages }
        </Swiper>
      </div>

      <div className={ st`container` }>
        <div className="grid">
          <div className={ st`intro` }>
            <h2 className={ st`title` }>{ listing.title }{ listingLocation.building_name && ' - ' + listingLocation.building_name }</h2>
            <p className={ st`subtitle` }>
              { listingLocation.district && listingLocation.district + ', ' }
              { listingLocation.city }
              { listingLocation.surface_area && ', ' + listingLocation.surface_area + 'm²' }
            </p>
            <div className={ st`description` }>
              <ParagraphsExpand content={ listing.description } />
            </div>
          </div>
          <div className={ st`aside` }>
            <div className={ st`box` }>
              <div className={ st`boxHeader` }>
                <div className={ st`boxPrice` }>
                  <small>From</small><h5>€{ listing.cheapest_price }</h5>
                </div>
                <div className={ st`boxButtons` }>
                  <button className="button button--inverted" onClick={ () => modalContext.setModalVisibleShare(true) }>
                    <img src="/images/icons/share.svg" />
                    <span>Share</span>
                  </button>
                  <Link href={ `${ router.asPath }#availability` }>
                    <button className="button button--primary">Book now</button>
                  </Link>
                </div>
              </div>
              <h6 className={ st`listingHeading` }>Listing details</h6>
              <div className={ st`listingDetails` }>
                <span>Available workspaces</span>
                <span>{ totalWorkSpaces }</span>
              </div>
              <div className={ st`listingDetails` }>
                <span>Category</span>
                <span>{ typeLabels[listingLocation.type] }</span>
              </div>
              <div className={ st`listingDetails` }>
                <span>Cancellation</span>
                <span>24 hours in advance</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={ st`section` } id="availability">
        <div className="container">
          <h5 className={ st`sectionHeading` }>Availability</h5>
          <Availability
            workspaces={ listing.listings_workspaces }
            listing={ listing }
            selectedDays={ appContext.selectedDays }
            personsAmount={ appContext.personsAmount }
          />
        </div>
      </div>

      <div className={ st`section` }>
        <div className="container">
          <h5 className={ st`sectionHeading` }>Shared amenities</h5>
          <Amenities
            amenities={ listing.listings_amenities.filter(amenity => amenity.status === 'active') }
          />
        </div>
      </div>

      <div className={ st`section sectionSecondary` }>
        <div className="container">
          <div className="grid">
            <div className="col-m6">
              <h5 className={ st`sectionHeading` }>Location</h5>
              <Map lat={ listingLocation.lat } lng={ listingLocation.lng } />
            </div>
            <div className="col-m6">
              <h5 className={ st`sectionHeading` }>Opening times</h5>
              <OpeningHours
                weekdays={ listingLocation.weekdays }
                check_in_start={ listingLocation.check_in_start }
                check_in_end={ listingLocation.check_in_end }
                check_out_end={ listingLocation.check_out_end }
              />
            </div>
          </div>
        </div>
      </div>

      <div className={ st`section sectionSecondary` }>
        <div className="container">
          <h5 className={ st`sectionHeading` }>Host&apos;s details</h5>
          <HostDetails
            rules={ listing.listings_rules }
          />
        </div>
      </div>

      <div className={ st`imageOverlay ${ imageOverlayVisible && 'imageOverlayVisible' }` }>
        <Button variant="inverted" className={ st`imageOverlayClose` } onClick={ () => setImageOverlayVisible(false) }>
          <img src="/images/icons/close.svg" />
          <span>Close</span>
        </Button>
        <div className={ st`slider` }>
          <Swiper
            key={ activeSlide }
            loop={ true }
            slidesPerView={ 1 }
            pagination={ {
              type: 'custom',
              renderCustom: (swiper, current, total) => {
                return current + ' / ' + total;
              },
            } }
            centeredSlides= { true }
            slidesPerColumn= { 1 }
            initialSlide= { activeSlide }
            spaceBetween= { 0 }
            onSlideChange= { (swiper) => {
              setActiveSlideLightBox(swiper.activeIndex - 1);
            } }
          >
            { renderImagesLightbox }
          </Swiper>
        </div>
      </div>
    </main>
  );
}
